/**
 * Audio Streaming Utilities
 * Provides token-based streaming URLs for meditation audio files
 * to prevent downloads while allowing playback.
 */

import { apiClient } from '@/utils/apiClient';

export interface AudioStreamingTokenResponse {
  token: string;
  streaming_url: string;
  expires_in: number;
}

/**
 * Get streaming token and URL for meditation audio file
 * This prevents downloads while allowing streaming playback
 *
 * @param relativePath - Relative path like "user123/session456.mp3"
 * @returns Promise<string> - Streaming URL with embedded token
 *
 * @example
 * const url = await getMeditationAudioStreamingUrl("user123/session456.mp3");
 * // Returns: "https://api.example.com/api/v1/audio/stream/user123/session456.mp3?token=..."
 */
export const getMeditationAudioStreamingUrl = async (relativePath: string): Promise<string> => {
  try {
    console.log(`🔐 Requesting streaming token for: ${relativePath}`);

    const response: AudioStreamingTokenResponse = await apiClient.get(
      `/audio/token/${relativePath}`
    );

    console.log(`✅ Streaming URL obtained for: ${relativePath}`);
    return response.streaming_url;
  } catch (error) {
    console.error('Failed to get streaming URL for', relativePath, error);

    // SECURITY: Do not fallback to direct URL - token-based access is mandatory
    // Throw error to prevent unauthorized access
    throw new Error(`Failed to obtain streaming token for ${relativePath}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Refresh an existing streaming token that is close to expiry
 * Allows extending playback for active users without interruption
 *
 * @param currentToken - The current streaming token to refresh
 * @returns Promise<AudioStreamingTokenResponse> - New token and streaming URL
 *
 * @example
 * const newToken = await refreshStreamingToken(currentToken);
 * // Returns: { token: "...", streaming_url: "...", expires_in: 3600 }
 */
export const refreshStreamingToken = async (currentToken: string): Promise<AudioStreamingTokenResponse> => {
  try {
    console.log(`🔄 Refreshing streaming token`);

    const response: AudioStreamingTokenResponse = await apiClient.post(
      `/audio/token/refresh`,
      { token: currentToken }
    );

    console.log(`✅ Token refreshed successfully`);
    return response;
  } catch (error) {
    console.error('Failed to refresh streaming token', error);
    throw new Error(`Failed to refresh streaming token: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get streaming URL for shared session audio
 * Uses share token authentication instead of streaming tokens
 *
 * @param shareToken - Share token from SharedSessionLink
 * @returns string - Streaming URL
 *
 * @example
 * const url = getSharedSessionStreamingUrl("abc123xyz");
 * // Returns: "https://api.example.com/api/v1/audio/shared/abc123xyz/stream"
 */
export const getSharedSessionStreamingUrl = (shareToken: string): string => {
  const baseUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin;
  return `${baseUrl}/audio/shared/${shareToken}/stream`;
};

/**
 * Check if a URL needs token-based streaming
 * Legacy URLs with full paths should be migrated to use streaming tokens
 *
 * @param audioUrl - Audio URL from session.audio_url
 * @returns boolean - True if URL needs token-based streaming
 */
export const needsStreamingToken = (audioUrl: string): boolean => {
  // If it's a relative path (no protocol, no /storage/), it needs a streaming token
  if (!audioUrl.includes('http') && !audioUrl.includes('/storage/')) {
    return true;
  }

  // Legacy full URLs should be migrated
  if (audioUrl.includes('/storage/meditation-audio/')) {
    return true;
  }

  return false;
};

/**
 * Extract relative path from audio URL
 * Handles both legacy full URLs and new relative paths
 *
 * @param audioUrl - Audio URL from session.audio_url
 * @returns string - Relative path like "user123/session456.mp3"
 *
 * @example
 * extractRelativePath("/storage/meditation-audio/user123/file.mp3")
 * // Returns: "user123/file.mp3"
 *
 * extractRelativePath("user123/file.mp3")
 * // Returns: "user123/file.mp3"
 */
export const extractRelativePath = (audioUrl: string): string => {
  // If already a relative path, return as-is
  if (!audioUrl.includes('/') || (!audioUrl.includes('http') && !audioUrl.includes('/storage/'))) {
    return audioUrl;
  }

  // Extract from full URL
  if (audioUrl.includes('/storage/meditation-audio/')) {
    const parts = audioUrl.split('/storage/meditation-audio/');
    return parts[parts.length - 1];
  }

  // Extract from /audio/ endpoint
  if (audioUrl.includes('/audio/')) {
    const parts = audioUrl.split('/audio/');
    return parts[parts.length - 1];
  }

  // Fallback: return as-is
  return audioUrl;
};
